import { ServiceId } from '@/constants';
import { generateUrlWithParams } from '@/helpers/generateURLWithParams';

/**
 * @description Returns the hero image URL based on the serviceId and seoSegmentId.
 * @param {Object} params - The parameters object.
 * @param {string} params.seoSegmentId - The seoSegmentId such as '/babysitters' or '/babysitters/austin-tx'.
 * @param {string} params.serviceId - The serviceId such as 'HOUSEKEEP'.
 * @returns {string|null} The hero image URL or null if not found.
 */

export const getHeroImage = ({
  seoSegmentId,
  serviceId,
}: {
  seoSegmentId: string;
  serviceId: string;
}): string | null => {
  // separate the segment from the rest of the url (e.g. /child-care/boston-ma -> /child-care)
  const segment = `/${seoSegmentId.split('/')[1]}`;
  const imQueryParams = {
    im: '',
  };

  const heroImageVerticalMap: Record<string, string | undefined> = Object.freeze({
    [ServiceId.CAREGIGSX]:
      'https://www.care.com/s/cms/web/visitor/greaterCareRebrand/searchDirectory/heroImages/personal-assistant-hero-collage.png',
    [ServiceId.CHILDCARE]:
      'https://www.care.com/s/cms/web/visitor/greaterCareRebrand/searchDirectory/heroImages/child-care-hero-collage.png',
    [ServiceId.PETCAREXX]:
      'https://www.care.com/s/cms/web/visitor/greaterCareRebrand/searchDirectory/heroImages/pet-care-hero-collage.png',
    [ServiceId.HOUSEKEEP]:
      'https://www.care.com/s/cms/web/visitor/greaterCareRebrand/searchDirectory/heroImages/housekeeping-hero-collage.png',
    [ServiceId.SENIRCARE]:
      'https://www.care.com/s/cms/web/visitor/greaterCareRebrand/searchDirectory/heroImages/senior-care-hero-collage.png',
    [ServiceId.TUTORINGX]:
      'https://www.care.com/s/cms/web/visitor/greaterCareRebrand/searchDirectory/heroImages/tutoring-hero-collage.png',
    [ServiceId.SPCLNEEDS]:
      'https://www.care.com/s/cms/web/visitor/greaterCareRebrand/searchDirectory/optimized/heroImages/special-needs-collage.png',
  });

  const specialtyHeroImageMap: Record<string, string | undefined> = Object.freeze({
    '/adult-care':
      'https://www.care.com/s/cms/web/visitor/greaterCareRebrand/searchDirectory/heroImages/adult-care-hero-collage.png',
    '/senior-living':
      'https://www.care.com/s/cms/web/visitor/greaterCareRebrand/searchDirectory/heroImages/senior-living-hero-collage.png',
    '/bird-sitters':
      'https://www.care.com/s/cms/web/visitor/greaterCareRebrand/searchDirectory/optimized/heroImages/bird-sitters-collage.png',
    '/cat-sitters':
      'https://www.care.com/s/cms/web/visitor/greaterCareRebrand/searchDirectory/optimized/heroImages/cat-sitters-collage.png',
    '/day-care':
      'https://www.care.com/s/cms/web/visitor/greaterCareRebrand/searchDirectory/optimized/heroImages/daycares-collage.png',
    '/newborn-nannies':
      'https://www.care.com/s/cms/web/visitor/greaterCareRebrand/searchDirectory/optimized/heroImages/newborn-nannies-collage.png',
    '/post-surgery-care':
      'https://www.care.com/s/cms/web/visitor/greaterCareRebrand/searchDirectory/optimized/heroImages/post-surgery-care-collage.png',
    '/senior-transportation':
      'https://www.care.com/s/cms/web/visitor/greaterCareRebrand/searchDirectory/optimized/heroImages/senior-transportation-collage.png',
    '/special-needs-adult-care':
      'https://www.care.com/s/cms/web/visitor/greaterCareRebrand/searchDirectory/optimized/heroImages/special-needs-adult-care-collage.png',
  });

  const heroImage = specialtyHeroImageMap[segment] ?? heroImageVerticalMap[serviceId];

  return heroImage ? generateUrlWithParams(heroImage, imQueryParams) : null;
};
